import { ApplicationView, raw, useController } from '@aha-app/mvc';
import React from 'react';
import { result } from 'lodash';
import ObjectMenuController from './ObjectMenuController';
import Section from './ObjectMenuSection';
import SectionButton from './SectionButton';
import ToolButton from '../../toolbars/ToolButton';
import { type SpriteSvgItem, type IconData } from '../../types';
import {
  testId,
  iconTitleize,
  iconTabItemStylusName,
  iconDisplayName,
  handlePromoClick,
} from '../../utils/objectMenu';
import styles from './objectMenuStyles.module.css';
import WithHint from '../../toolbars/WithHint';
import { type ObjectMenuSection, type TabProps } from './ObjectMenu';
import { iconComponent } from '../../utils/icons';
import { TOOLTIP_DELAY_GENERAL } from '../../constants';
import { type PromoLinkType, type PromoLinkValue } from '../../shared-types';

// This ensures that we show a maximum of 4 rows at the top level for each section
const TOP_LEVEL_FA_LIMIT = 24;
const TOP_LEVEL_SPRITE_LIMIT = 20;

export type IconTabItem = IconData | SpriteSvgItem;

const getSectionItems = (
  topLevel: boolean,
  sections: ObjectMenuSection<IconTabItem>[],
  items: IconTabItem[],
  isFaSection?: boolean
) => {
  const itemLimit = isFaSection ? TOP_LEVEL_FA_LIMIT : TOP_LEVEL_SPRITE_LIMIT;
  let sectionItems = topLevel ? items.slice(0, itemLimit) : items;
  let i = 0;
  let section = sections[i];
  if (topLevel && sectionItems.length < itemLimit) {
    while (sectionItems.length < itemLimit && section) {
      if (section) {
        sectionItems = sectionItems.concat(
          section.items.slice(0, itemLimit - sectionItems.length)
        );
      } else {
        break;
      }
      section = sections[++i];
    }
  }
  return sectionItems;
};

const IconsTab: React.FC<TabProps> = ({ onCollapseToggle }) => {
  const controller = useController(ObjectMenuController);
  const { backButtonVisible, topLevel } = controller;
  const { stylusIcon, stylusSpriteIcon, shapeDragManager } =
    controller.whiteboardController;

  return (
    <div data-drag-abort>
      {controller
        .activeSections<IconTabItem>()
        .map(
          ({
            title,
            items,
            sections,
            isFaSection,
            promoLink = '' as PromoLinkType,
            showNew = false,
            promoTooltipSelector = '',
          }) => {
            const sectionItems = getSectionItems(topLevel, sections, items);

            const stylus = isFaSection ? stylusIcon : stylusSpriteIcon;

            const disabledSection = Boolean(promoLink);
            const promoLinkVal = result<PromoLinkValue>(
              { promoLink },
              'promoLink'
            );

            return (
              <Section
                key={title}
                title={iconTitleize(title, true)}
                promoLink={promoLinkVal}
                showNew={showNew}
                promoTooltipSelector={promoTooltipSelector}
                onCollapseToggle={onCollapseToggle}
              >
                {sectionItems.map(item => {
                  const className = stylus.toolButtonClass();
                  const iconName = iconTabItemStylusName(item);
                  const name = iconDisplayName(item);

                  return (
                    <WithHint
                      key={iconName}
                      name={name}
                      delay={[TOOLTIP_DELAY_GENERAL, 0]}
                    >
                      <span>
                        <ToolButton
                          className={className}
                          draggable={!promoLinkVal}
                          onDragStart={e => {
                            stylus.options.iconName = iconName;
                            shapeDragManager.startDrag(e, stylus);
                          }}
                          onClick={event => {
                            if (disabledSection && promoLinkVal) {
                              handlePromoClick(promoLinkVal);
                            } else {
                              stylus.options.iconName = iconName;
                              controller.actionCloseMenu(raw(stylus));
                            }
                            event.stopPropagation();
                          }}
                          testId={testId(name)}
                        >
                          {iconComponent(item)}
                        </ToolButton>
                      </span>
                    </WithHint>
                  );
                })}
                {!!sections.length && (
                  <SectionButton
                    title={`All ${iconTitleize(title)}`}
                    onClick={() => {
                      if (disabledSection && promoLinkVal) {
                        handlePromoClick(promoLinkVal);
                      } else {
                        controller.actionPushSections(sections);
                      }
                    }}
                  />
                )}
              </Section>
            );
          }
        )}
      {backButtonVisible && !topLevel && (
        <SectionButton
          className={styles.objectMenuSectionButtonBackButton}
          title='All icons'
          onClick={() => controller.actionPopSections()}
        />
      )}
    </div>
  );
};

export default ApplicationView(IconsTab);
